import { useEffect, useState } from "react";
import { ItemList, Item } from "@/components/ui/item";
import { Spinner } from "@/components/ui/spinner";
import { Stat, StatGrid } from "@/components/ui/stat";
import { Frame, FrameHeader, FrameTitle } from "@/components/ui/frame";
import { useAuth } from "@clerk/react";
import { BanknoteIcon, PlusIcon, TriangleAlertIcon } from "lucide-react";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useFundingStore } from "@/stores/eve/fundingStore";
import type { EveFundingLedgerRow } from "@/types/eve";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";
import { Switch } from "@/components/ui/switch";

/**
 * "Client Funds" in the sidebar's Org. Usage card (platform-wallet orgs
 * only): a usage-style row - spent-of-deposited progress, available on the
 * right - that opens the funds dialog with the full breakdown, ledger
 * history, and (CS only, per the server's Clerk staff-flag verdict) the
 * inline Record Deposit form. Org-level money lives here, not on any
 * campaign tab. Visibility: staff (uplodio_member) always see it so they
 * can record the deposit that creates grade_funding; clients see it only
 * once grade_funding exists on the org. On a Grade-unconnected org, staff
 * instead get the "Enable client funding" control (the frontend counterpart
 * of scripts/connect-grade.ts --platform).
 */

const USD = { style: "currency" as const, currency: "USD", maximumFractionDigits: 2 };
// Sidebar row: whole dollars so "spent / deposited" stays compact.
const USD_WHOLE = { style: "currency" as const, currency: "USD", maximumFractionDigits: 0 };

const LEDGER_BADGE: Record<EveFundingLedgerRow["type"], BadgeProps["variant"]> = {
  deposit: "success",
  payout: "info",
  adjustment: "warning",
  refund: "error",
};

export default function ClientFunds() {
  const { getToken, orgId } = useAuth();
  const { mode, canRecord, funding, ledger, isRecording, recordDeposit, fetchFunding } =
    useFundingStore();

  const [amountText, setAmountText] = useState("");
  const [note, setNote] = useState("");
  const [recordOpen, setRecordOpen] = useState(false);

  // Runs on mount and again on org switch (orgId) — the funding data is
  // org-scoped via the token, so a switch must refetch.
  useEffect(() => {
    void (async () => {
      const token = await getToken();
      if (token) await fetchFunding(token);
    })();
  }, [getToken, fetchFunding, orgId]);

  // Unconnected org ("none"): staff get the enable control, clients see
  // nothing. Own-wallet orgs and unfetched state (null) render nothing.
  if (mode === "none") return canRecord ? <EnableClientFunding /> : null;
  if (mode !== "platform") return null;

  // Staff (canRecord = the server's Clerk uplodio_member verdict) always see
  // the pot — their first recorded deposit is what creates grade_funding on
  // the org. Clients only see it once it exists (funding.configured), so a
  // not-yet-funded org doesn't show them a confusing $0/$0 row.
  if (!canRecord && !funding?.configured) return null;

  const available = funding?.available ?? 0;
  // Gross: approved deals plus Grade's estimated fee, since every payout
  // debits amount + fee — bare prices would read ~5% too optimistic.
  const committed = funding?.committed_gross ?? 0;
  const deposited = funding?.deposited_total ?? 0;
  const spent = funding?.spent_total ?? 0;
  const feePct = `${((funding?.fee_rate ?? 0.05) * 100).toFixed(1).replace(/\.0$/, "")}%`;
  const committedCount = funding?.committed_count ?? 0;
  const exhausted = available <= 0;
  const overcommitted = !exhausted && committed > available;

  // Spent-of-deposited, usage-row semantics: nothing spent = empty bar
  // (an unfunded org shows $0/$0 at 0% - the dialog's alert carries the story).
  const spentPct = deposited > 0 ? Math.min((spent / deposited) * 100, 100) : 0;

  const amount = Number.parseFloat(amountText);
  const amountValid = Number.isFinite(amount) && amount !== 0 && Math.abs(amount) <= 1_000_000;

  const handleRecord = async () => {
    const token = await getToken();
    if (!token || !amountValid) return;
    const ok = await recordDeposit(amount, note, token);
    if (ok) {
      setRecordOpen(false);
      setAmountText("");
      setNote("");
    }
  };

  return (
    <Dialog>
      <ScopeWrapper className="w-full">
        <DialogTrigger className="flex flex-col w-full items-center gap-1 hover:bg-muted p-1.5 rounded-sm cursor-pointer">
          <div className="flex flex-row items-center justify-between text-xs w-full text-foreground/80">
            <p className="font-normal">Funds spent</p>
            <span className="flex flex-row items-center gap-0.5 font-mono font-medium">
              <span>{spent.toLocaleString("en-US", USD_WHOLE)}</span>
              <span>/</span>
              <span>{deposited.toLocaleString("en-US", USD_WHOLE)}</span>
            </span>
          </div>
          <Progress
            variant="segmented"
            value={spentPct}
            className="h-2"
            max={100}
          // bgColor="bg-info/20"
          // color="bg-info"
          />
        </DialogTrigger></ScopeWrapper>

      {/* sm:-prefixed on purpose - DialogContent's own sm:max-w-lg only
          yields to a class in the SAME tailwind-merge group. */}
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Funds
          </DialogTitle>
          <DialogDescription>
            Deposits from you wired us. Uplodio funds this org's creator
            payouts. <br />
            Available = deposits − confirmed payouts (fees included).
            Committed = every deal you approved, paid or not, plus Grade's
            estimated fee on top.
          </DialogDescription>
        </DialogHeader>

        {exhausted && (
          <Alert variant="destructive">
            <AlertTitle>
              <TriangleAlertIcon />
              {funding?.configured ? "Out of client funding" : "No client funding recorded"}
            </AlertTitle>
            <AlertDescription>
              Payouts are blocked and the payment phase can't be enabled.
              {canRecord
                ? " Record the client's deposit below to unblock."
                : " Ask your CS contact to record the client's deposit."}
            </AlertDescription>
          </Alert>
        )}
        {overcommitted && (
          <Alert variant="warning">
            <AlertTitle>
              <TriangleAlertIcon />
              Approved deals outrun the funding
            </AlertTitle>
            <AlertDescription>
              {committed.toLocaleString("en-US", USD)} in approved deals incl.
              est. fees against {available.toLocaleString("en-US", USD)}{" "}
              available. The last
              payouts will refuse unless more funding lands.
            </AlertDescription>
          </Alert>
        )}

        <StatGrid className="md:grid-cols-4">
          <Stat
            label="Available"
            value={available}
            format={USD}
            highlight
            tooltip="Deposited − Spent. What's left in the pot right now for creator payouts. Committed deals are not subtracted yet."
          />
          <Stat
            label="Committed + fees"
            value={committed}
            format={USD}
            tooltip={`Every deal you approved (${committedCount} deal${committedCount === 1 ? "" : "s"}, from the approve click through completed, paid or not) at the agreed price, plus Grade's estimated ${feePct} fee. Paid deals stay here and also show in Spent. Waiting-approval deals are not included.`}
          />
          <Stat
            label="Deposited"
            value={deposited}
            format={USD}
            tooltip="Total the client wired to Uplodio, as recorded by your CS contact. Corrections (negative entries) are already netted in."
          />
          <Stat
            label="Spent"
            value={spent}
            format={USD}
            tooltip="Confirmed creator payouts including Grade's actual fee, minus refunds of canceled payouts."
          />
        </StatGrid>

        {ledger.length > 0 && (
          <Frame>
            <FrameHeader>
              <FrameTitle>Ledger</FrameTitle>
            </FrameHeader>
            {/* block!: Radix's display:table viewport wrapper sizes to the
                widest row, so long notes never truncated and the dialog
                scrolled sideways. */}
            <ItemList className="max-h-64 [&_[data-slot=scroll-area-viewport]>div]:block!">
              {ledger.map((row) => (
                <Item key={row.id} variant="row" size="sm" className="flex-nowrap gap-2 text-xs">
                  <Badge variant={LEDGER_BADGE[row.type]} size="sm" className="capitalize shrink-0">
                    {row.type}
                  </Badge>
                  <span className="text-muted-foreground truncate min-w-0">
                    {row.note ??
                      (row.type === "payout"
                        ? "Creator payout"
                        : row.type === "refund"
                          ? "Payout canceled"
                          : "")}
                  </span>
                  <span className="ml-auto font-mono shrink-0">
                    {row.amount >= 0 ? "+" : ""}
                    {row.amount.toLocaleString("en-US", USD)}
                  </span>
                  <span className="text-muted-foreground shrink-0 w-20 text-right">
                    {new Date(row.at).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                </Item>
              ))}
            </ItemList>
          </Frame>
        )}

        {/* CS only: opens the record dialog nested over this one. */}
        {canRecord && (
          <AlertDialog open={recordOpen} onOpenChange={setRecordOpen}>
            <AlertDialogTrigger asChild>
              <Button variant="neutral" className="w-full">
                <PlusIcon className="size-3.5" />
                Record Deposit
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="max-w-lg">
              <AlertDialogHeader>
                <AlertDialogTitle>Record a client deposit</AlertDialogTitle>
                <AlertDialogDescription>
                  The amount the client wired to Uplodio. It becomes available
                  for payouts immediately. A negative amount records a
                  correction.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="funding_amount">Amount (USD)</Label>
                  <Input
                    id="funding_amount"
                    type="number"
                    step="0.01"
                    placeholder="5000.00"
                    value={amountText}
                    onChange={(e) => setAmountText(e.target.value)}
                    disabled={isRecording}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="funding_note">
                    Note{" "}
                    <span className="text-muted-foreground text-xs font-normal">
                      (optional)
                    </span>
                  </Label>
                  <Input
                    id="funding_note"
                    maxLength={500}
                    placeholder="e.g. Invoice #142, wire received Aug 20"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    disabled={isRecording}
                  />
                </div>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isRecording}>Cancel</AlertDialogCancel>
                <Button onClick={handleRecord} variant="neutral" disabled={!amountValid || isRecording}>
                  {isRecording ? (
                    <Spinner className="size-3.5" />
                  ) : (
                    <BanknoteIcon className="size-3.5" />
                  )}
                  Record Deposit
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Staff-only sidebar row shown on Grade-unconnected orgs: one confirmed
 * click marks the org Uplodio-managed (platform wallet), after which the
 * normal funds row takes this slot. The server re-checks the Clerk staff
 * flag, so this control is cosmetic, not the security boundary.
 */
function EnableClientFunding() {
  const { getToken } = useAuth();
  const { isEnabling, enableClientFunding } = useFundingStore();
  const [open, setOpen] = useState(false);

  const handleEnable = async () => {
    const token = await getToken();
    if (!token) return;
    const ok = await enableClientFunding(token);
    if (ok) setOpen(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <ScopeWrapper className="w-full">
        <AlertDialogTrigger className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-sm p-1.5 text-xs text-foreground/80 hover:bg-muted mt-1">
          <span className="font-normal">Client funding</span>
          <span className="flex items-center gap-1 font-medium text-muted-foreground">
            <Switch size="xs" checked={false} />
          </span>
        </AlertDialogTrigger>
      </ScopeWrapper>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>Enable client funding?</AlertDialogTitle>
          <AlertDialogDescription>
            Marks this organization as Uplodio-managed: creator payouts ride
            Uplodio's Grade wallet, the funds pot appears here in the sidebar,
            and the payment phase can be enabled. Record the client's first
            deposit right after. Only the Uplodio team sees this control.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isEnabling}>Cancel</AlertDialogCancel>
          <Button onClick={handleEnable} variant="neutral" disabled={isEnabling}>
            {isEnabling ? (
              <Spinner className="size-3.5" />
            ) : (
              <BanknoteIcon className="size-3.5" />
            )}
            Enable
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
